import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hero banner photos — crossfade every 7s behind the hero copy.
// Desktop shows the wide banner crops; mobile shows square crops (shot at the same
// sessions) so the photo fills the narrower hero card without an awkward center-crop.
const HERO_IMAGES = ["/banner/banner-1.jpg", "/banner/banner-2.jpg", "/banner/banner-3.jpg"];
const HERO_IMAGES_MOBILE = ["/banner/banner-mobile-1.jpg", "/banner/banner-mobile-2.jpg", "/banner/banner-mobile-3.jpg"];

const Home = () => {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [roomCode, setRoomCode] = useState("");
  const [hostName, setHostName] = useState(user?.name || "");
  const [mode, setMode] = useState("solo"); // "solo" | "teams"
  const [voiceEnabled, setVoiceEnabled] = useState(false); // mics on/off — adjustable again later in the lobby
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [heroImageIndex, setHeroImageIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setHeroImageIndex((i) => (i + 1) % HERO_IMAGES.length);
    }, 7000);
    return () => clearInterval(id);
  }, []);

  const handleCreateRoom = async (e) => {
    e.preventDefault();
    setError("");
    if (!roomCode.trim() || !hostName.trim()) {
      setError("Please enter both room code and your name");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/rooms/create`, {
        room_code: roomCode.toUpperCase(),
        host_name: hostName,
        mode,
        voice_enabled: voiceEnabled,
      });
      // Anonymous rooms return a host_token — persist it so this browser retains host control across reloads.
      if (res.data?.is_anonymous && res.data?.host_token) {
        const canonical = res.data.room_code || roomCode.trim().toUpperCase();
        localStorage.setItem(`tf_host_token_${canonical}`, res.data.host_token);
        if (res.data.expires_at) {
          localStorage.setItem(`tf_host_expires_${canonical}`, res.data.expires_at);
        }
      }
      navigate(`/room/${encodeURIComponent(roomCode.trim().toUpperCase())}?name=${encodeURIComponent(hostName)}&host=true`);
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      if (status === 402) {
        setError(detail || "Free plan limit reached. Upgrade to Pro for unlimited rooms.");
      } else {
        setError(detail || "Failed to create room");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById("create-room")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const tier = user?.pro_tier || (user?.is_pro ? "lifetime" : "free");
  const isPro = tier === "monthly" || tier === "lifetime";
  const tierLabel = tier === "monthly" ? "PRO · MONTHLY" : tier === "lifetime" ? "PRO · LIFETIME" : "FREE";

  return (
    <div className="min-h-screen w-full relative overflow-x-hidden text-white">
      {/* Background: same ambient glow treatment as the approved landing-page redesign
          (replaces the old static bg-hero.png — no image request, no fixed photo to clash
          with brighter/darker screens). */}
      <div
        className="fixed inset-0 z-[-1]"
        style={{
          background:
            "radial-gradient(900px 560px at 8% -6%, rgba(255,0,85,0.32), transparent 60%), radial-gradient(900px 560px at 94% 10%, rgba(0,240,255,0.24), transparent 60%), radial-gradient(700px 520px at 85% 92%, rgba(0,240,255,0.14), transparent 60%), #050508",
        }}
      />

      {/* Top bar */}
      <div className="relative z-20 w-full">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 short:py-2 flex items-center justify-between gap-3">
          <div
            className="flex items-baseline gap-1.5 text-lg"
            style={{ fontFamily: "Anton, sans-serif" }}
          >
            <span className="text-[#FF0055]">TAP</span>
            <span className="text-[#00F0FF]">FIRST</span>
          </div>
          <div className="flex items-center gap-3">
            {!loading && user && (
              <div className="flex items-center gap-2">
                <div
                  className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-sm flex items-center gap-2"
                  data-testid="user-badge"
                >
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FF0055] to-[#00F0FF] flex items-center justify-center text-xs font-semibold">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden sm:inline">{user.name}</span>
                  <span
                    className={`px-2 py-0.5 text-xs font-bold rounded-full ${
                      isPro ? "bg-[#FFEA00] text-black" : "bg-white/10 text-white/70"
                    }`}
                    data-testid="tier-badge"
                  >
                    {tierLabel}
                  </span>
                </div>
                <button
                  onClick={logout}
                  className="px-3 py-1.5 text-[#A1A1AA] hover:text-white text-sm transition-colors"
                  data-testid="logout-button"
                >
                  Logout
                </button>
              </div>
            )}
            {!loading && !user && (
              <Link
                to="/login"
                className="px-4 py-1.5 bg-white/10 border border-white/20 rounded-full text-sm hover:bg-white/20 transition-all"
                data-testid="top-login-button"
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="relative z-10 flex flex-col items-center px-4 pt-2 pb-8 short:pt-1 short:pb-4">
        {/* HERO — rotating photo banner behind the copy: three images crossfade every 7s,
            with a scrim that fades from solid near the text to transparent toward the photo. */}
        <div
          className="relative w-full max-w-6xl mb-14 sm:mb-20 overflow-hidden rounded-3xl border border-white/10"
          data-testid="hero-banner"
        >
          {/* Mobile: square crops. Desktop: wide crops. Both share heroImageIndex so they
              stay in sync. Pure opacity cross-fade — deliberately NO z-index here: these
              layers must stay below the scrim divs (next, in DOM order) at all times, or
              the active photo paints over the dark scrim and washes out the text. */}
          <div className="absolute inset-0 lg:hidden">
            {HERO_IMAGES_MOBILE.map((src, i) => (
              <img
                key={src}
                src={src}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                style={{
                  opacity: i === heroImageIndex ? 1 : 0,
                  transition: "opacity 3000ms ease-in-out",
                }}
              />
            ))}
          </div>
          <div className="absolute inset-0 hidden lg:block">
            {HERO_IMAGES.map((src, i) => (
              <img
                key={src}
                src={src}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                style={{
                  opacity: i === heroImageIndex ? 1 : 0,
                  transition: "opacity 3000ms ease-in-out",
                }}
              />
            ))}
          </div>

          {/* Legibility scrim — mobile: top-to-bottom so text reads over the full-width photo;
              desktop: left-to-right so the photo shows through on the right. */}
          <div
            className="absolute inset-0 lg:hidden"
            style={{
              background:
                "linear-gradient(180deg, rgba(5,5,8,0.55) 0%, rgba(5,5,8,0.82) 55%, rgba(5,5,8,0.96) 100%)",
            }}
          />
          <div
            className="absolute inset-0 hidden lg:block"
            style={{
              background:
                "linear-gradient(90deg, rgba(5,5,8,0.97) 0%, rgba(5,5,8,0.88) 36%, rgba(5,5,8,0.45) 62%, rgba(5,5,8,0.08) 100%)",
            }}
          />

          <div className="relative z-10 px-6 sm:px-10 lg:px-14 py-12 sm:py-16 lg:py-24 max-w-2xl mx-auto lg:mx-0">
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
              <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.14em] uppercase text-[#00F0FF] mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FFEA00]" style={{ boxShadow: "0 0 8px #FFEA00" }} />
                Real-time buzzer game
              </div>

              <h1
                className="text-4xl sm:text-6xl lg:text-7xl short:text-3xl short:sm:text-4xl font-primary mb-5 tracking-tight leading-[0.96]"
                style={{ fontFamily: "Anton, sans-serif" }}
                data-testid="home-title"
              >
                FIRST TO <span className="text-[#FF0055]">TAP</span> <span className="text-[#00F0FF]">WINS.</span>
              </h1>
              <p className="text-base sm:text-lg text-[#A1A1AA] leading-relaxed max-w-md mb-8">
                The buzzer game for trivia nights, classrooms, and competitions. Host a room, share a code, and the fastest tap wins &mdash; on any device, in seconds.
              </p>

              <div className="flex items-center gap-4 flex-wrap justify-center lg:justify-start mb-3">
                <button
                  type="button"
                  onClick={scrollToForm}
                  className="px-9 py-4 bg-[#00F0FF] text-black font-primary text-base tracking-wide rounded-xl shadow-[0_0_20px_rgba(0,240,255,0.4)] hover:scale-[1.02] active:scale-95 transition-all uppercase"
                  style={{ fontFamily: "Anton, sans-serif" }}
                  data-testid="hero-cta-button"
                >
                  Create Free Room
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/join")}
                  className="text-sm text-[#A1A1AA] underline underline-offset-2 hover:text-white transition-colors"
                  data-testid="hero-join-link"
                >
                  Have a code? Join a room
                </button>
              </div>
              <p className="text-xs font-bold tracking-wide text-[#00F0FF] mb-8" data-testid="hero-trust-line">
                NO SIGNUP REQUIRED &nbsp;&middot;&nbsp; NO APP TO INSTALL &nbsp;&middot;&nbsp; FREE TO START
              </p>

              <div className="flex gap-3 flex-wrap justify-center lg:justify-start">
                <div className="flex items-center gap-2 border border-white/10 bg-white/[0.06] backdrop-blur-sm rounded-xl px-4 py-2.5 text-xs font-semibold">
                  <span className="w-3.5 h-3.5 rounded-[5px] bg-[#00F0FF]/15 border border-[#00F0FF]/45" />
                  Works on any device
                </div>
                <div className="flex items-center gap-2 border border-white/10 bg-white/[0.06] backdrop-blur-sm rounded-xl px-4 py-2.5 text-xs font-semibold">
                  <span className="w-3.5 h-3.5 rounded-[5px] bg-[#00F0FF]/15 border border-[#00F0FF]/45" />
                  Unlimited players
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CREATE ROOM — scroll target for the hero CTA. Form logic below is unchanged. */}

        {/* ANONYMOUS USER — full create-room form, works without signing in.
            Anon rooms auto-expire in 24h and a host_token is stored in localStorage for reclaim. */}
        {!loading && !user && (
          <div id="create-room" className="scroll-mt-6 backdrop-blur-2xl bg-black/40 border border-white/10 rounded-3xl p-6 sm:p-8 short:p-4 shadow-2xl w-full max-w-md mb-14 sm:mb-20">
            <h2
              className="text-3xl font-primary text-center mb-2 text-[#00F0FF]"
              style={{ fontFamily: "Anton, sans-serif" }}
            >
              CREATE ROOM
            </h2>

            <div
              className="bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-xl px-3 py-2 mb-5 text-center"
              data-testid="guest-tier-banner"
            >
              <p className="text-xs text-[#00F0FF] uppercase tracking-wider font-bold">
                Guest mode
              </p>
              <p className="text-sm text-white/80 mt-1">
                Host + 3 players · Room expires in 24h
              </p>
              <Link
                to="/signup"
                className="inline-block mt-2 text-xs text-[#FFEA00] underline hover:text-white"
                data-testid="guest-banner-signup-link"
              >
                Sign up free to keep rooms forever →
              </Link>
            </div>

            <form onSubmit={handleCreateRoom} className="space-y-4">
              {/* Mode toggle: Solo vs Teams */}
              <div className="flex bg-white/5 border border-white/10 rounded-xl p-1" data-testid="mode-toggle-anon">
                <button
                  type="button"
                  onClick={() => setMode("solo")}
                  className={`flex-1 py-2 rounded-lg font-semibold tracking-wide transition-all text-sm ${
                    mode === "solo"
                      ? "bg-[#00F0FF] text-black shadow-[0_0_15px_rgba(0,240,255,0.5)]"
                      : "text-[#A1A1AA] hover:text-white"
                  }`}
                  data-testid="mode-solo-anon"
                >
                  🧍 SOLO
                </button>
                <button
                  type="button"
                  onClick={() => setMode("teams")}
                  className={`flex-1 py-2 rounded-lg font-semibold tracking-wide transition-all text-sm ${
                    mode === "teams"
                      ? "bg-[#FFEA00] text-black shadow-[0_0_15px_rgba(255,234,0,0.5)]"
                      : "text-[#A1A1AA] hover:text-white"
                  }`}
                  data-testid="mode-teams-anon"
                >
                  👥 TEAMS
                </button>
              </div>
              <div className="flex bg-white/5 border border-white/10 rounded-xl p-1" data-testid="voice-toggle-anon">
                <button
                  type="button"
                  onClick={() => setVoiceEnabled(false)}
                  className={`flex-1 py-2 rounded-lg font-semibold tracking-wide transition-all text-sm ${
                    !voiceEnabled
                      ? "bg-white text-black shadow-[0_0_15px_rgba(255,255,255,0.3)]"
                      : "text-[#A1A1AA] hover:text-white"
                  }`}
                  data-testid="voice-off-anon"
                >
                  🔇 NO MICS
                </button>
                <button
                  type="button"
                  onClick={() => setVoiceEnabled(true)}
                  className={`flex-1 py-2 rounded-lg font-semibold tracking-wide transition-all text-sm ${
                    voiceEnabled
                      ? "bg-[#9D4EDD] text-black shadow-[0_0_15px_rgba(157,78,221,0.5)]"
                      : "text-[#A1A1AA] hover:text-white"
                  }`}
                  data-testid="voice-on-anon"
                >
                  🎙️ WITH MICS
                </button>
              </div>
              <p className="text-xs text-[#A1A1AA] text-center -mt-1">
                You can change this again in the lobby before starting.
              </p>
              <input
                type="text"
                placeholder="ROOM NAME"
                value={roomCode}
                onChange={(e) => setRoomCode(e.target.value)}
                className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-4 short:py-2 text-center text-xl font-secondary tracking-wide focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition-all"
                maxLength={100}
                data-testid="create-room-code-input"
              />
              <input
                type="text"
                placeholder="YOUR NAME"
                value={hostName}
                onChange={(e) => setHostName(e.target.value)}
                className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-4 short:py-2 text-center text-xl font-secondary focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition-all"
                maxLength={100}
                data-testid="create-room-name-input"
              />
              {error && (
                <div className="text-[#FF0055] text-center text-sm" data-testid="create-room-error">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-4 short:py-2.5 mt-6 short:mt-3 bg-[#00F0FF] text-black font-primary text-2xl short:text-xl tracking-wide rounded-xl shadow-[0_0_20px_rgba(0,240,255,0.4)] hover:scale-[1.02] active:scale-95 transition-all uppercase disabled:opacity-50"
                style={{ fontFamily: "Anton, sans-serif" }}
                data-testid="create-room-button"
              >
                {submitting ? "CREATING..." : "CREATE ROOM"}
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-white/10 text-center">
              <p className="text-[#A1A1AA] text-sm mb-3">Joining instead?</p>
              <Link
                to="/join"
                className="block w-full py-3 bg-white/5 border border-white/20 text-[#00F0FF] rounded-xl hover:bg-white/10 transition-all text-sm font-semibold tracking-wide"
                data-testid="go-to-join-button"
              >
                JOIN EXISTING ROOM
              </Link>
              <p className="text-xs text-[#A1A1AA] mt-4">
                Already have an account?{" "}
                <Link to="/login" className="text-[#00F0FF] hover:underline">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        )}

        {/* SIGNED IN USER (Free OR Pro) — same create-room form */}
        {!loading && user && (
          <div id="create-room" className="scroll-mt-6 backdrop-blur-2xl bg-black/40 border border-white/10 rounded-3xl p-6 sm:p-8 short:p-4 shadow-2xl w-full max-w-md mb-14 sm:mb-20">
            <h2
              className="text-3xl font-primary text-center mb-2 text-[#00F0FF]"
              style={{ fontFamily: "Anton, sans-serif" }}
            >
              CREATE ROOM
            </h2>

            {!isPro && (
              <div
                className="bg-[#FFEA00]/10 border border-[#FFEA00]/30 rounded-xl px-3 py-2 mb-5 text-center"
                data-testid="free-tier-banner"
              >
                <p className="text-xs text-[#FFEA00] uppercase tracking-wider font-bold">
                  Free plan
                </p>
                <p className="text-sm text-white/80 mt-1">
                  Host + 3 players · 1 room · Neon theme only
                </p>
                <Link
                  to="/upgrade"
                  className="inline-block mt-2 text-xs text-[#FFEA00] underline hover:text-white"
                  data-testid="free-banner-upgrade-link"
                >
                  Upgrade for unlimited players, rooms & themes →
                </Link>
              </div>
            )}

            <form onSubmit={handleCreateRoom} className="space-y-4">
              {/* Mode toggle: Solo vs Teams */}
              <div className="flex bg-white/5 border border-white/10 rounded-xl p-1" data-testid="mode-toggle">
                <button
                  type="button"
                  onClick={() => setMode("solo")}
                  className={`flex-1 py-2 rounded-lg font-semibold tracking-wide transition-all text-sm ${
                    mode === "solo"
                      ? "bg-[#00F0FF] text-black shadow-[0_0_15px_rgba(0,240,255,0.5)]"
                      : "text-[#A1A1AA] hover:text-white"
                  }`}
                  data-testid="mode-solo"
                >
                  🧍 SOLO
                </button>
                <button
                  type="button"
                  onClick={() => setMode("teams")}
                  className={`flex-1 py-2 rounded-lg font-semibold tracking-wide transition-all text-sm ${
                    mode === "teams"
                      ? "bg-[#FFEA00] text-black shadow-[0_0_15px_rgba(255,234,0,0.5)]"
                      : "text-[#A1A1AA] hover:text-white"
                  }`}
                  data-testid="mode-teams"
                >
                  👥 TEAMS
                </button>
              </div>
              <div className="flex bg-white/5 border border-white/10 rounded-xl p-1" data-testid="voice-toggle">
                <button
                  type="button"
                  onClick={() => setVoiceEnabled(false)}
                  className={`flex-1 py-2 rounded-lg font-semibold tracking-wide transition-all text-sm ${
                    !voiceEnabled
                      ? "bg-white text-black shadow-[0_0_15px_rgba(255,255,255,0.3)]"
                      : "text-[#A1A1AA] hover:text-white"
                  }`}
                  data-testid="voice-off"
                >
                  🔇 NO MICS
                </button>
                <button
                  type="button"
                  onClick={() => setVoiceEnabled(true)}
                  className={`flex-1 py-2 rounded-lg font-semibold tracking-wide transition-all text-sm ${
                    voiceEnabled
                      ? "bg-[#9D4EDD] text-black shadow-[0_0_15px_rgba(157,78,221,0.5)]"
                      : "text-[#A1A1AA] hover:text-white"
                  }`}
                  data-testid="voice-on"
                >
                  🎙️ WITH MICS
                </button>
              </div>
              <p className="text-xs text-[#A1A1AA] text-center -mt-1">
                You can change this again in the lobby before starting.
              </p>
              <input
                type="text"
                placeholder="ROOM NAME"
                value={roomCode}
                onChange={(e) => setRoomCode(e.target.value)}
                className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-4 short:py-2 text-center text-xl font-secondary tracking-wide focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition-all"
                maxLength={100}
                data-testid="create-room-code-input"
              />
              <input
                type="text"
                placeholder="YOUR NAME"
                value={hostName}
                onChange={(e) => setHostName(e.target.value)}
                className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-4 short:py-2 text-center text-xl font-secondary focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition-all"
                maxLength={100}
                data-testid="create-room-name-input"
              />
              {error && (
                <div className="text-[#FF0055] text-center text-sm" data-testid="create-room-error">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-4 short:py-2.5 mt-6 short:mt-3 bg-[#00F0FF] text-black font-primary text-2xl short:text-xl tracking-wide rounded-xl shadow-[0_0_20px_rgba(0,240,255,0.4)] hover:scale-[1.02] active:scale-95 transition-all uppercase disabled:opacity-50"
                style={{ fontFamily: "Anton, sans-serif" }}
                data-testid="create-room-button"
              >
                {submitting ? "CREATING..." : "CREATE ROOM"}
              </button>
            </form>
            <div className="mt-8 pt-6 border-t border-white/10 text-center">
              <p className="text-[#A1A1AA] text-sm mb-3">Joining instead?</p>
              <Link
                to="/join"
                className="block w-full py-3 bg-white/5 border border-white/20 text-[#00F0FF] rounded-xl hover:bg-white/10 transition-all text-sm font-semibold tracking-wide"
                data-testid="go-to-join-button"
              >
                JOIN EXISTING ROOM
              </Link>
            </div>
          </div>
        )}

        {loading && (
          <div className="w-12 h-12 border-4 border-[#00F0FF] border-t-transparent rounded-full animate-spin mb-14 sm:mb-20" />
        )}

        {/* BENEFITS */}
        <div className="w-full max-w-5xl mb-14 sm:mb-20 px-2">
          <h2
            className="text-2xl sm:text-3xl text-center mb-8"
            style={{ fontFamily: "Anton, sans-serif" }}
          >
            WHY HOSTS LOVE IT
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                title: "NO APP NEEDED",
                body: "Guests just scan a QR code and they're playing — nothing to install.",
                icon: (
                  <path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h3v3h-3zM20 14v3M17 20h4" />
                ),
              },
              {
                title: "WORKS ON ANY PHONE",
                body: "Any browser, any device — even Grandma's iPad works fine.",
                icon: <path d="M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2ZM11 18h2" />,
              },
              {
                title: "INSTANT, FAIR RESULTS",
                body: "First tap wins, every time. No arguing about who was first.",
                icon: <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />,
              },
              {
                title: "FREE TO START",
                body: "Host + 3 players free, right now — upgrade only if you need more.",
                icon: <path d="M20.59 13.41 12 22l-9-9 8.59-8.59A2 2 0 0 1 13 4h6a1 1 0 0 1 1 1v6a2 2 0 0 1-.41 1.41Z" />,
              },
            ].map((b) => (
              <div
                key={b.title}
                className="bg-white/5 border border-white/10 rounded-2xl p-5 text-left transition-all duration-200 hover:bg-white/10 hover:border-[#00F0FF]/40 hover:-translate-y-0.5"
              >
                <div className="w-11 h-11 rounded-xl bg-[#00F0FF]/10 border border-[#00F0FF]/30 flex items-center justify-center mb-4">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#00F0FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {b.icon}
                  </svg>
                </div>
                <h3 className="text-sm font-semibold tracking-wide mb-2">{b.title}</h3>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* SEE IT IN ACTION — illustrated mockups (CSS, no photo assets to manage) of the phone buzzer and stage reveal */}
        <div className="w-full max-w-5xl mb-14 sm:mb-20 px-2">
          <h2
            className="text-2xl sm:text-3xl text-center mb-2"
            style={{ fontFamily: "Anton, sans-serif" }}
          >
            SEE IT IN ACTION
          </h2>
          <p className="text-center text-[#A1A1AA] text-sm mb-8">What your guests see, and what your TV shows.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.06] to-white/[0.02] p-6 sm:p-8 transition-all duration-200 hover:border-[#00F0FF]/40">
              <p className="text-xs text-[#00F0FF] tracking-[0.08em] font-bold uppercase mb-5">
                On every phone
              </p>
              <div
                className="relative w-[220px] h-[400px] mx-auto border-[6px] border-[#1c1c22] rounded-[34px] overflow-hidden shadow-2xl flex flex-col items-center justify-center text-center px-6"
                style={{
                  background:
                    "radial-gradient(160px 220px at 15% 10%, rgba(255,0,85,0.45), transparent 60%), radial-gradient(160px 220px at 90% 85%, rgba(0,240,255,0.4), transparent 60%), #0b0b10",
                }}
                data-testid="demo-phone-mock"
              >
                {[
                  { top: "12%", left: "20%", color: "#FF0055" },
                  { top: "18%", left: "78%", color: "#FFEA00" },
                  { top: "30%", left: "55%", color: "#00F0FF" },
                  { top: "68%", left: "15%", color: "#00F0FF" },
                  { top: "78%", left: "82%", color: "#FF0055" },
                  { top: "88%", left: "35%", color: "#FFEA00" },
                ].map((dot, i) => (
                  <span
                    key={i}
                    className="absolute w-1.5 h-1.5 rounded-full"
                    style={{ top: dot.top, left: dot.left, background: dot.color, boxShadow: `0 0 6px ${dot.color}` }}
                  />
                ))}
                <p className="text-2xl mb-1" style={{ fontFamily: "Anton, sans-serif" }}>YOU'RE UP!</p>
                <p className="text-3xl mb-2 text-[#FFEA00]" style={{ fontFamily: "Anton, sans-serif" }}>Maya</p>
                <p className="text-xs text-[#00F0FF] mb-6">✨ Pressed First!! ✨</p>
                <p className="text-[10px] text-[#A1A1AA]">Waiting for host to reset...</p>
              </div>
              <p className="text-center text-sm text-[#A1A1AA] mt-5">
                The whole screen is the buzzer &mdash; and the win moment hits just as hard.
              </p>
            </div>
            <div className="rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.06] to-white/[0.02] p-6 sm:p-8 transition-all duration-200 hover:border-[#00F0FF]/40">
              <p className="text-xs text-[#00F0FF] tracking-[0.08em] font-bold uppercase mb-5">
                On the TV / projector
              </p>
              <div
                className="relative w-full h-[400px] border-[6px] border-[#16161b] rounded-2xl overflow-hidden flex flex-col items-center justify-center text-center px-6"
                style={{
                  background:
                    "linear-gradient(115deg, transparent 38%, rgba(255,0,85,0.35) 46%, rgba(255,0,85,0.12) 54%, transparent 60%), linear-gradient(65deg, transparent 38%, rgba(0,240,255,0.32) 46%, rgba(0,240,255,0.1) 54%, transparent 60%), #050508",
                }}
                data-testid="demo-stage-mock"
              >
                <p className="text-xs tracking-[0.25em] text-[#00F0FF] font-bold mb-3">BUZZER IN</p>
                <p
                  className="text-5xl sm:text-6xl text-white mb-6"
                  style={{ fontFamily: "Anton, sans-serif", textShadow: "0 0 24px rgba(0,240,255,0.5)" }}
                >
                  Maya
                </p>
                <div className="flex items-center gap-3 bg-black/50 border border-white/10 rounded-xl px-4 py-2">
                  <span>🏅</span>
                  <span className="text-sm font-semibold">Maya</span>
                  <span className="text-sm text-[#FFEA00] ml-2" style={{ fontFamily: "Anton, sans-serif" }}>0</span>
                </div>
              </div>
              <p className="text-center text-sm text-[#A1A1AA] mt-4">
                Cinematic winner reveal your whole room can see.
              </p>
            </div>
          </div>
        </div>

        {/* PERFECT FOR — quick-scan list of use cases, matching the approved mockup */}
        <div className="w-full max-w-5xl mb-14 sm:mb-20 px-2">
          <h2
            className="text-2xl sm:text-3xl text-center mb-8"
            style={{ fontFamily: "Anton, sans-serif" }}
          >
            PERFECT FOR
          </h2>
          <div className="flex flex-wrap justify-center gap-2">
            {["Trivia Nights", "Classrooms", "Family Game Night", "Team Meetings", "Parties & Celebrations", "Streamers"].map((t) => (
              <span
                key={t}
                className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm transition-all duration-200 hover:bg-[#00F0FF]/10 hover:border-[#00F0FF]/40 hover:text-white cursor-default"
              >
                {t}
              </span>
            ))}
          </div>
        </div>

        {/* PRICING TEASER */}
        <div className="w-full max-w-2xl mb-14 sm:mb-20 px-2">
          <h2
            className="text-2xl sm:text-3xl text-center mb-8"
            style={{ fontFamily: "Anton, sans-serif" }}
          >
            SIMPLE PRICING
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-2xl p-6 border border-white/10 bg-white/5">
              <p className="text-xs tracking-[0.1em] text-[#A1A1AA] font-bold mb-2">FREE</p>
              <p className="text-4xl mb-4" style={{ fontFamily: "Anton, sans-serif" }}>$0</p>
              <ul className="space-y-2 text-sm text-[#A1A1AA]">
                <li>Host + 3 players</li>
                <li>1 room</li>
                <li>Neon theme only</li>
              </ul>
            </div>
            <div className="rounded-2xl p-6 border border-[#FFEA00]/30 bg-gradient-to-b from-[#FFEA00]/10 to-white/[0.03]">
              <p className="text-xs tracking-[0.1em] text-[#FFEA00] font-bold mb-2">PRO</p>
              <p className="text-4xl mb-4" style={{ fontFamily: "Anton, sans-serif" }}>
                $6.99<span className="text-sm text-[#A1A1AA] font-secondary"> /mo &middot; or $49 lifetime</span>
              </p>
              <ul className="space-y-2 text-sm text-[#A1A1AA]">
                <li>Unlimited players &amp; rooms</li>
                <li>Custom logo &amp; branding</li>
                <li>CSV export of results</li>
              </ul>
              <Link to="/upgrade" className="block text-center mt-4 text-xs text-[#00F0FF] underline" data-testid="pricing-teaser-upgrade-link">
                See full pricing →
              </Link>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="w-full max-w-5xl mt-4 pt-8 border-t border-white/10 text-center px-2">
          <p className="text-sm text-[#A1A1AA] mb-3">
            <span className="text-[#FF0055]">TAP</span> <span className="text-[#00F0FF]">FIRST</span> &middot; tap-first.com
          </p>
          <div className="flex justify-center gap-6 text-xs text-[#A1A1AA] pb-8">
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <Link to="/upgrade" className="hover:text-white">Pricing</Link>
            <Link to="/feedback" className="hover:text-white">Feedback</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
